import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useCallback } from 'react';
export function RadarChart({ scores, size = 300 }) {
    const canvasRef = useRef(null);
    const MAX = Math.max(...scores.map(s => s.sfp), 10);
    const labels = scores.slice(0, 5).map(s => s.modelId);
    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas)
            return;
        const ctx = canvas.getContext('2d');
        if (!ctx)
            return;
        const dpr = window.devicePixelRatio || 1;
        canvas.width = size * dpr;
        canvas.height = size * dpr;
        canvas.style.width = `${size}px`;
        canvas.style.height = `${size}px`;
        ctx.scale(dpr, dpr);
        const cx = size / 2;
        const cy = size / 2;
        const radius = Math.min(cx, cy) * 0.75;
        const numPoints = labels.length;
        ctx.clearRect(0, 0, size, size);
        // Grid - brutal style
        ctx.strokeStyle = '#333333';
        ctx.lineWidth = 1;
        for (let level = 1; level <= 5; level++) {
            ctx.beginPath();
            const r = (radius / 5) * level;
            for (let i = 0; i < numPoints; i++) {
                const angle = (i / numPoints) * Math.PI * 2 - Math.PI / 2;
                const x = cx + r * Math.cos(angle);
                const y = cy + r * Math.sin(angle);
                if (i === 0)
                    ctx.moveTo(x, y);
                else
                    ctx.lineTo(x, y);
            }
            ctx.closePath();
            ctx.stroke();
        }
        // Axes - brutal
        ctx.strokeStyle = '#4a4a4a';
        ctx.fillStyle = '#999999';
        ctx.font = '11px "Fira Code", "JetBrains Mono", monospace';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        for (let i = 0; i < numPoints; i++) {
            const angle = (i / numPoints) * Math.PI * 2 - Math.PI / 2;
            const x = cx + radius * Math.cos(angle);
            const y = cy + radius * Math.sin(angle);
            const tx = cx + (radius + 18) * Math.cos(angle);
            const ty = cy + (radius + 18) * Math.sin(angle);
            ctx.beginPath();
            ctx.moveTo(cx, cy);
            ctx.lineTo(x, y);
            ctx.stroke();
            ctx.fillText(labels[i].slice(0, 12), tx, ty);
        }
        // Data - accent cyan
        ctx.strokeStyle = '#06b6d4';
        ctx.fillStyle = 'rgba(6, 182, 212, 0.25)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        for (let i = 0; i < numPoints; i++) {
            const s = scores[i];
            const normalized = (s.sfp / MAX) * radius;
            const angle = (i / numPoints) * Math.PI * 2 - Math.PI / 2;
            const x = cx + normalized * Math.cos(angle);
            const y = cy + normalized * Math.sin(angle);
            if (i === 0)
                ctx.moveTo(x, y);
            else
                ctx.lineTo(x, y);
        }
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        // Points - brutal square
        ctx.fillStyle = '#06b6d4';
        for (let i = 0; i < numPoints; i++) {
            const s = scores[i];
            const normalized = (s.sfp / MAX) * radius;
            const angle = (i / numPoints) * Math.PI * 2 - Math.PI / 2;
            const x = cx + normalized * Math.cos(angle);
            const y = cy + normalized * Math.sin(angle);
            ctx.fillRect(x - 4, y - 4, 8, 8);
        }
    }, [scores, size, MAX, labels]);
    useEffect(() => {
        draw();
    }, [draw]);
    return (_jsxs("div", { className: "relative flex justify-center", children: [_jsx("canvas", { ref: canvasRef, width: size, height: size, style: { border: '2px solid #333333' } }), _jsx("div", { className: "absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 flex gap-2 flex-wrap justify-center", children: scores.slice(0, 5).map((s, i) => {
                    return (_jsxs("span", { className: "text-xs px-2 py-1 border-2 border-border bg-surface font-mono", children: [String(i + 1).padStart(2, '0'), ". ", s.modelId, " (", s.sfp.toFixed(1), ")"] }, s.modelId));
                }) })] }));
}
